import React from 'react';
import { Link, NavLink } from 'react-router-dom';

import { Form, Formik, FormikProps } from 'formik';

import SearchFieldAuto from '@app/components/UI/Forms/SearchField/SearchFieldAuto';
import { eventToLink } from '@app/utils/eventToLink';
import '@app/scss/components/blockSideBar.scss';

interface Item {
	url: string;
	name: string;
	title?: string;
	[key: string | number]: any;
}

interface SidebarProps {
	onSetIssue: ((item: Item | any) => void) | Record<string | number, (item: Item) => void>;
	topics?: Array<Item>;
	searchFieldPlaceHolder?: string;
	topicsInner?: Record<(string | number), (Array<any> | undefined)>;
	typeTopicsInner?: string;
	isSearchFieldNeeded?: boolean;
	newsPage?: boolean;
	searchFieldValue?: string;
	onSubmit?: (val: string) => void;
	setSearchFieldValue?: (val: string) => void;
}

export const additionalResources = [
	{
		name: 'HowIKnow',
		url: 'how-will-i-know-it-is-time',
		title: "How Will I Know It's Time",
	},
	{
		name: 'Calendar',
		url: 'quality-of-life-calendar.pdf',
		title: 'Pet Quality of Life Calendar',
	},
	{
		name: 'Newsletter',
		url: 'newsletter',
	},
	{
		name: 'Blog',
		url: 'blog',
	},
	{
		name: 'Videos',
		url: 'videos',
	},
	{
		name: 'FAQ',
		url: 'faq',
	},
];

const SidebarWithCaptions: React.FC<SidebarProps> = ({
	searchFieldPlaceHolder = '',
	onSetIssue,
	topics,
	onSubmit,
	typeTopicsInner,
	topicsInner,
	isSearchFieldNeeded = true,
	newsPage = false,
	searchFieldValue,
	setSearchFieldValue,
}) => {
	const debounceTimer = React.useRef<NodeJS.Timeout | null>(null);

	const debouncedSubmit = React.useCallback((value: string) => {
		if (debounceTimer.current) {
			clearTimeout(debounceTimer.current);
		}

		debounceTimer.current = setTimeout(() => {
			onSubmit && onSubmit(value);
		}, 800);
	}, [onSubmit]);

	React.useEffect(() => {
		return () => {
			if (debounceTimer.current) {
				clearTimeout(debounceTimer.current);
			}
		};
	}, []);

	return (
		<div className="block__sidebar">
			<Formik onSubmit={(values) => {}} initialValues={{ searchQuery: '' }}>
				{(formikBag: FormikProps<any>) => {
					return (
						<Form autoComplete="off">
							{isSearchFieldNeeded && (
								<SearchFieldAuto
									name="searchQuery"
									placeholder={searchFieldPlaceHolder}
									formikBag={formikBag}
									onSelect={(value, option) => {}}
									onChange={(value) => {
										formikBag.setFieldValue('searchQuery', value);
										setSearchFieldValue && setSearchFieldValue(value);

										if (!value) {
											onSubmit && onSubmit('');
											return;
										}

										debouncedSubmit(value);
									}}
									onSearch={() => {
										if (searchFieldValue) {
											onSubmit && onSubmit(searchFieldValue);
										}
									}}
									searchButton
									buttonType="submit"
									allowClear
								/>
							)}
							<div className="block__sidebar-nav">
								{topics && topics.length > 0 && (
									<ul className="block__sidebar-nav-list sidebar-menu">

										{newsPage && (
											<li className="sidebar-menu__section">
												<h6 className="sidebar-menu__section-title block__sidebar-nav-list-caption">In the News</h6>
												<ul className="sidebar-menu__section-content">
													{topics.map((item, indexInner) => (
														<li key={indexInner} className="sidebar-menu__section-content__item">
															<NavLink
																to={`/in-the-news/${item.url}`}
																className={({ isActive }) => `
																	sidebar-menu__section-content__item-link ${isActive ? 'active' : ''}
																`}
																onClick={() => typeof onSetIssue === 'function' && onSetIssue(item)}
																end
															>
																{item.name}
															</NavLink>
														</li>
													))}
												</ul>
											</li>
										)}

										{!newsPage && (
											<>
												<li className="sidebar-menu__section">
													<h6 className="sidebar-menu__section-title block__sidebar-nav-list-caption">
														Resource Topics
													</h6>
													<ul className="sidebar-menu__section-content">
														{topics.map((item, indexInner) => (
															<li key={indexInner} className="sidebar-menu__section-content__item">
																<NavLink
																	to={`/resource-center/${item.url}`}
																	className={({ isActive }) => `
																		sidebar-menu__section-content__item-link ${isActive ? 'active' : ''}
																	`}
																	onClick={() => {
																		const key = 'Common';
																		onSetIssue?.[key]?.(item);
																	}}
																	end
																>
																	{item.name}
																</NavLink>
															</li>
														))}
													</ul>
												</li>
												<li className="sidebar-menu__section">
													<h6 className="sidebar-menu__section-title block__sidebar-nav-list-caption">
														Additional Resources
													</h6>
													<ul className="sidebar-menu__section-content">
														{additionalResources.map((resource, indexInner) => (
															<li key={indexInner} className="sidebar-menu__section-content__item">
																{resource.url !== 'quality-of-life-calendar.pdf' ? (
																	<NavLink
																		to={`/${resource.url}`}
																		className={({ isActive }) => `
																			sidebar-menu__section-content__item-link ${isActive ? 'active' : ''}
																		`}
																		onClick={() => onSetIssue?.[resource.name]?.(resource)}
																		end
																	>
																		{resource.title ? resource.title : resource.name}
																	</NavLink>
																) : (
																	<Link
																		to={`/${resource.url}`}
																		target="_blank"
																		className="sidebar-menu__section-content__item-link"
																		onClick={() => {
																			onSetIssue?.[resource.name]?.(resource);
																			eventToLink(resource.title ? resource.title : resource.name);
																		}}
																	>
																		{resource.title ? resource.title : resource.name}
																	</Link>
																)}

																{typeTopicsInner && topicsInner && resource.url === typeTopicsInner && (
																	<ul className="sidebar-menu__section-content sidebar-menu__section-content--second-level">
																		{topicsInner[resource.name] ? (
																			topicsInner?.[resource?.name]?.map((topic, indexInner) => {
																				return (
																					<li
																						key={indexInner}
																						className="sidebar-menu__section-content__item"
																					>
																						<NavLink
																							to={`/${
																								resource.url
																							}/${topic.name
																								.toLowerCase()
																								.replace(/[&]/g, 'and')
																								.replace(/\s+/g, '-')}`}
																							className={({ isActive }) => `
																								sidebar-menu__section-content__item-link ${isActive ? 'active' : ''}
																							`}
																							onClick={() => onSetIssue?.[resource.name]?.(topic)}
																							end
																						>
																							{topic.name}
																						</NavLink>
																					</li>
																				);
																			})
																		) : null}
																	</ul>
																)}
															</li>
														))}
													</ul>
												</li>
											</>
										)}

									</ul>
								)}
							</div>
						</Form>
					);
				}}
			</Formik>
		</div>
	);
};

export default SidebarWithCaptions;
